// src/hooks/useGoalForecast.ts
// Custom hook for Goal Forecast data fetching and realtime updates

import { useEffect, useCallback, useState } from 'react';
// import { useGoalForecastStore } from '@/stores/goalForecastStore';
import { useRealtime } from './useRealtime';
import { useGoalForecastStore, type GoalProjection, type WeightLog } from '../stores/goalForecastStore';
import { supabase } from '../lib/supabase';
import { usePlan } from './usePlan';
// import type { GoalProjection, WeightLog } from '@/stores/goalForecastStore';

interface UseGoalForecastOptions {
  // enabled?: boolean;
  refetchInterval?: number; // Optional: auto-refetch interval in ms (e.g., 60000 for 1 min)
}

export function useGoalForecast(options: UseGoalForecastOptions = {}) {
  const { refetchInterval } = options;
  const { activePlan } = usePlan()
  const [enabled, setEnabled] = useState(!!activePlan?.id)

  const {
    projection,
    weightHistory,
    profile,
    goalType,
    activePlanId,
    isLoading,
    isCalculating,
    error,
    lastFetchedAt,
    setProjection,
    setWeightHistory,
    setProfile,
    setActivePlanId,
    setLoading,
    setCalculating,
    setError,
    setLastFetchedAt,
  } = useGoalForecastStore();

  // Fetch all goal forecast data
  const fetchGoalForecast = useCallback(async (force = false) => {
    if (!enabled) return;

    // Prevent unnecessary refetches (only refetch if > 30 seconds since last fetch)
    if (!force && lastFetchedAt && Date.now() - lastFetchedAt < 30000) {
      // console.log('[useGoalForecast] Skipping fetch (recent data available)');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('No authenticated user');
      }

      // console.log('[useGoalForecast] Fetching data for user:', user.id);

      // 1. Get active plan
      const { data: activePlan, error: planError } = await supabase
        .from('nutrition_plans')
        .select('id, goal')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .single();

      if (planError || !activePlan) {
        // console.log('[useGoalForecast] No active plan found');
        setActivePlanId(null);
        setProjection(null);
        setLoading(false);
        setLastFetchedAt(Date.now());
        return;
      }

      setActivePlanId(activePlan.id);

      // 2. Get user profile
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('current_weight, target_weight, goal')
        .eq('id', user.id)
        .single();

      if (profileError) {
        throw new Error('Failed to fetch profile');
      }

      setProfile(profileData);

      // 3. Get latest projection for active plan
      const { data: projectionData, error: projectionError } = await supabase
        .from('goal_projections')
        .select('*')
        .eq('plan_id', activePlan.id)
        .order('calculated_at', { ascending: false })
        .limit(1)
        .maybeSingle(); // Use maybeSingle to handle no results gracefully

      if (projectionError && projectionError.code !== 'PGRST116') {
        // PGRST116 = no rows returned (not an error)
        // console.error('[useGoalForecast] Projection error:', projectionError);
      }

      setProjection(projectionData);

      // 4. Get weight history for active plan
      const { data: weights, error: weightsError } = await supabase
        .from('weight_history')
        .select('id, weight, created_at, current_plan_id')
        .eq('user_id', user.id)
        .eq('current_plan_id', activePlan.id)
        .order('created_at', { ascending: true });

      if (weightsError) {
        // console.error('[useGoalForecast] Weights error:', weightsError);
      } else {
        setWeightHistory(weights || []);
      }

      setLastFetchedAt(Date.now());
      // console.log('[useGoalForecast] Data fetched successfully');

    } catch (err: any) {
      // console.error('[useGoalForecast] Error:', err);
      setError(err.message || 'Failed to load goal forecast');
    } finally {
      setLoading(false);
    }
  }, [lastFetchedAt, setLoading, setError, setProjection, setWeightHistory, setProfile, setActivePlanId, setLastFetchedAt]);

  // Trigger calculation of new projection
  const calculateProjection = useCallback(async () => {
    if (!enabled) return;

    try {
      setCalculating(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('No authenticated user');
      }

      // console.log('[useGoalForecast] Triggering projection calculation...');

      const { data, error } = await supabase.functions.invoke('calculate-goal-forecast', {
        body: { user_id: user.id }
      });

      if (error) {
        throw error;
      }

      if (data?.success && data?.projection) {
        // console.log('[useGoalForecast] Projection calculated successfully');
        // Refetch to get latest data
        await fetchGoalForecast(true);
      } else if (data?.error) {
        throw new Error(data.error);
      }

    } catch (err: any) {
      // console.error('[useGoalForecast] Calculation error:', err);
      setError(err.message || 'Failed to calculate projection');
    } finally {
      setCalculating(false);
    }
  }, [fetchGoalForecast, setCalculating, setError]);

  // Initial fetch on mount
  useEffect(() => {
    if (enabled) {
      fetchGoalForecast();
    }
  }, [enabled]); // Only run on mount or when enabled changes

  // Optional: Periodic refetch
  useEffect(() => {
    if (!enabled || !refetchInterval) return;

    const interval = setInterval(() => {
      fetchGoalForecast(true);
    }, refetchInterval);

    return () => clearInterval(interval);
  }, [enabled, refetchInterval, fetchGoalForecast]);

  // Realtime subscription: goal_projections table
  useRealtime<GoalProjection>({
    table: 'goal_projections',
    filter: activePlanId ? `plan_id=eq.${activePlanId}` : undefined,
    enabled: enabled && !!activePlanId,
    onInsert: (payload) => {
      // console.log('[useGoalForecast] New projection inserted:', payload);
      setProjection(payload);
      setLastFetchedAt(Date.now());
    },
    onUpdate: (payload) => {
      // console.log('[useGoalForecast] Projection updated:', payload);
      setProjection(payload);
      setLastFetchedAt(Date.now());
    },
  });

  // Realtime subscription: weight_history table
  useRealtime<WeightLog>({
    table: 'weight_history',
    filter: activePlanId ? `current_plan_id=eq.${activePlanId}` : undefined,
    enabled: enabled && !!activePlanId,
    onInsert: (payload) => {
      // console.log('[useGoalForecast] New weight log inserted:', payload);
      // Add to weight history
      setWeightHistory([...weightHistory, payload].sort((a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      ));
      // Trigger recalculation after weight log
      calculateProjection();
    },
    onUpdate: (payload) => {
      // console.log('[useGoalForecast] Weight log updated:', payload);
      // Update weight history
      setWeightHistory(
        weightHistory.map(w => w.id === payload.id ? payload : w)
      );
    },
    onDelete: (payload) => {
      // console.log('[useGoalForecast] Weight log deleted:', payload.old);
      // Remove from weight history
      setWeightHistory(
        weightHistory.filter(w => w.id !== payload.old.id)
      );
    },
  });

  return {
    // Data
    projection,
    weightHistory,
    profile,
    goalType,
    activePlanId,

    // Loading states
    isLoading,
    isCalculating,
    error,

    // Actions
    fetchGoalForecast: fetchGoalForecast,
    refetch: () => fetchGoalForecast(true),
    calculate: calculateProjection,
  };
}